import { useEffect, useState } from 'react';

/**
 * The header's save status (crossmarc/steward-app#1886), the same on both forms. A green dot
 * once saved; from `sm` up the words sit beside it, and below `sm` the dot stands alone with
 * the words kept for screen readers. A failure always shows its words, and its retry, at every
 * width: a save that did not happen is not something to shrink to a dot.
 */
export type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  /** `at` unset reads plain "Saved"; set, it reads "Saved just now" and then a clock time. */
  | { kind: 'saved'; at?: number }
  | { kind: 'error'; text: string };

export function SaveIndicator({ status, onRetry }: { status: SaveStatus; onRetry: () => void }) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (status.kind !== 'saved' || status.at === undefined) return;
    const t = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [status]);

  if (status.kind === 'error') {
    return (
      <span className="text-[12px] text-danger font-sans" role="status" data-testid="form-save-state">
        {status.text}{' '}
        <button type="button" onClick={onRetry} className="bg-transparent border-none p-0 underline cursor-pointer text-danger text-[12px]">Try again</button>
      </span>
    );
  }
  let text = '';
  if (status.kind === 'saving') text = 'Saving...';
  else if (status.kind === 'saved') {
    text = status.at === undefined ? 'Saved'
      : Date.now() - status.at < 60_000 ? 'Saved just now'
      : `Saved at ${new Date(status.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-slate font-sans" data-testid="form-save-state" role="status">
      {status.kind === 'saved' && <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-success-fill" />}
      {status.kind === 'saving' && <span aria-hidden className="inline-block h-2 w-2 animate-pulse rounded-full bg-slate/60" />}
      <span className="sr-only sm:not-sr-only">{text}</span>
    </span>
  );
}
